import React from 'react';
import { 
  Flame, Rocket, Shield, Sun, Star, Moon, Award, Map, Lock, Zap
} from 'lucide-react';

const iconMap = {
  Flame, Rocket, Shield, Sun, Star, Moon, Award, Map, Zap
};

const ALL_POSSIBLE_BADGES = [
  { 
    id: 'consistent', 
    name: 'Consistent', 
    desc: 'Maintain a 5-day streak', 
    icon: 'Flame',
    color: 'text-amber-500 dark:text-amber-400',
    bg: 'bg-amber-500/10 dark:bg-amber-500/20',
    border: 'border-amber-500/40 dark:border-amber-500/60',
    glow: 'shadow-[0_0_20px_rgba(245,158,11,0.35)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] border-amber-400/60'
  },
  { 
    id: 'unstoppable', 
    name: 'Unstoppable', 
    desc: 'Maintain a 20-day streak', 
    icon: 'Rocket',
    color: 'text-indigo-500 dark:text-indigo-400',
    bg: 'bg-indigo-500/10 dark:bg-indigo-500/20',
    border: 'border-indigo-500/40 dark:border-indigo-500/60',
    glow: 'shadow-[0_0_20px_rgba(99,102,241,0.35)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] border-indigo-400/60'
  },
  { 
    id: 'ironclad', 
    name: 'Ironclad', 
    desc: 'Maintain a 50-day streak', 
    icon: 'Shield',
    color: 'text-emerald-500 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
    border: 'border-emerald-500/40 dark:border-emerald-500/60',
    glow: 'shadow-[0_0_20px_rgba(16,185,129,0.35)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] border-emerald-400/60'
  },
  { 
    id: 'early_bird', 
    name: 'Early Bird', 
    desc: 'Punch in before your shift starts', 
    icon: 'Sun',
    color: 'text-orange-500 dark:text-orange-400',
    bg: 'bg-orange-500/10 dark:bg-orange-500/20',
    border: 'border-orange-500/40 dark:border-orange-500/60',
    glow: 'shadow-[0_0_20px_rgba(249,115,22,0.35)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] border-orange-400/60'
  },
  { 
    id: 'vip_scan', 
    name: 'Crystal Clear', 
    desc: 'Get a 92%+ AI face match', 
    icon: 'Star',
    color: 'text-yellow-400 dark:text-yellow-300',
    bg: 'bg-yellow-500/10 dark:bg-yellow-500/20',
    border: 'border-yellow-400/40 dark:border-yellow-400/60',
    glow: 'shadow-[0_0_20px_rgba(250,204,21,0.4)] hover:shadow-[0_0_30px_rgba(250,204,21,0.6)] border-yellow-300/60'
  },
  { 
    id: 'night_owl', 
    name: 'Night Owl', 
    desc: 'Log late shift overtime', 
    icon: 'Moon',
    color: 'text-purple-400 dark:text-purple-300',
    bg: 'bg-purple-500/10 dark:bg-purple-500/20',
    border: 'border-purple-400/40 dark:border-purple-400/60',
    glow: 'shadow-[0_0_20px_rgba(192,132,252,0.35)] hover:shadow-[0_0_30px_rgba(192,132,252,0.5)] border-purple-300/60'
  },
  { 
    id: 'weekend_warrior', 
    name: 'Weekend Warrior', 
    desc: 'Punch in on a Sunday', 
    icon: 'Award',
    color: 'text-rose-500 dark:text-rose-400',
    bg: 'bg-rose-500/10 dark:bg-rose-500/20',
    border: 'border-rose-500/40 dark:border-rose-500/60',
    glow: 'shadow-[0_0_20px_rgba(244,63,94,0.35)] hover:shadow-[0_0_30px_rgba(244,63,94,0.5)] border-rose-400/60'
  },
  { 
    id: 'field_ranger', 
    name: 'Field Ranger', 
    desc: 'Work from 3+ different sites', 
    icon: 'Map',
    color: 'text-sky-500 dark:text-sky-400',
    bg: 'bg-sky-500/10 dark:bg-sky-500/20',
    border: 'border-sky-500/40 dark:border-sky-500/60',
    glow: 'shadow-[0_0_20px_rgba(14,165,233,0.35)] hover:shadow-[0_0_30px_rgba(14,165,233,0.5)] border-sky-400/60'
  },
];

const Achievements = ({ gamification }) => {
  const userBadges = gamification?.badges?.filter(b => b.id !== 'starter') || [];

  const earnedBadges = ALL_POSSIBLE_BADGES.filter(badge => 
    userBadges.some(ub => ub.id === badge.id)
  ).map(badge => {
    const rawData = userBadges.find(ub => ub.id === badge.id);
    return { ...badge, ...rawData };
  });

  const lockedBadges = ALL_POSSIBLE_BADGES.filter(badge => 
    !userBadges.some(ub => ub.id === badge.id)
  );

  return (
    <div className="backdrop-blur-xl bg-white/60 dark:bg-[#0B101E]/60 border border-slate-200/50 dark:border-slate-700/50 rounded-[2rem] p-6 shadow-xl shadow-slate-200/40 dark:shadow-none flex-[2] min-w-0 flex flex-col relative overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2">
          <div className="p-1.5 bg-indigo-500/10 rounded-md">
            <Award className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> 
          </div>
          Recent Achievements 
          <span className="bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 px-2.5 py-0.5 rounded-full ml-2 font-extrabold text-[11px]">
            {earnedBadges.length} / {ALL_POSSIBLE_BADGES.length}
          </span>
        </h3>
      </div>
      
      <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar flex-1 items-center snap-x snap-mandatory">
        {/* Glowing Unlocked Badges */}
        {earnedBadges.map((badge, i) => {
          const IconComponent = iconMap[badge.icon] || Award;
          return (
            <div 
              key={`earned-${i}`} 
              className={`snap-center shrink-0 flex flex-col justify-center gap-3 px-5 py-4 rounded-2xl border ${badge.bg} ${badge.glow} backdrop-blur-md transition-all duration-300 hover:-translate-y-1 cursor-default min-w-[160px] h-full relative overflow-hidden group`}
            >
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-white/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500"></div>

              <div className={`p-3 rounded-xl bg-white/90 dark:bg-slate-900/90 shadow-md w-fit backdrop-blur-sm relative z-10`}>
                <IconComponent className={`w-6 h-6 ${badge.color} drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]`} />
              </div>
              <div className="relative z-10">
                <p className={`text-base font-extrabold ${badge.color} tracking-tight`}>{badge.name}</p>
                <p className="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mt-1 opacity-90">
                  {badge.title || 'Unlocked'}
                </p>
              </div>
            </div>
          );
        })}

        {/* Locked Badges */}
        {lockedBadges.map((badge, i) => {
          const IconComponent = iconMap[badge.icon] || Award;
          return (
            <div 
              key={`locked-${i}`} 
              className="snap-center shrink-0 flex flex-col justify-center gap-3 px-5 py-4 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700/80 bg-slate-100/40 dark:bg-slate-800/20 opacity-60 hover:opacity-100 transition-all duration-300 cursor-help min-w-[160px] h-full group relative"
            >
              <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-900/90 dark:bg-slate-800/90 backdrop-blur-md text-white text-[10px] font-bold px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20 shadow-xl border border-slate-700">
                Goal: {badge.desc}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900/90 dark:bg-slate-800/90 rotate-45 border-r border-b border-slate-700"></div>
              </div>

              <div className="p-3 rounded-xl bg-slate-200/80 dark:bg-slate-800/80 text-slate-400 shadow-inner w-fit">
                <IconComponent className="w-6 h-6 opacity-60" />
              </div>
              <div>
                <p className="text-base font-extrabold text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  {badge.name} <Lock className="w-3 h-3 opacity-50" />
                </p>
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-1">Locked</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Achievements;